import { type DocEmailDraft, type DocTextDraft, EMAIL_DOC_LABELS, type EmailDocKind, type EmailItem } from "@nmt/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { errorMessage, get, post } from "../api";
import { Alert, Badge, Button, Dialog, Field, Loading, Panel, PanelHeader, TextAreaField, cx, formatDateTime } from "./ui";

/** "Email it": opens the email window for a document whose customer link is in hand. */
export function EmailItButton({ kind, id, link, jobId, className }: { kind: EmailDocKind; id: string; link: string; jobId: string; className?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button className={className} onClick={() => setOpen(true)}>
        Email it
      </Button>
      {open && <EmailDialog kind={kind} id={id} link={link} jobId={jobId} onClose={() => setOpen(false)} />}
    </>
  );
}

/** To, CC, subject and message from the template (editable), the link added as a button; sent from the rep's address. */
export function EmailDialog({ kind, id, link, jobId, onClose, purpose }: { kind: EmailDocKind; id: string; link: string; jobId: string; onClose: () => void; purpose?: "overdue" }) {
  const qc = useQueryClient();
  const draft = useQuery({ queryKey: ["email-draft", kind, id, link, purpose], queryFn: () => post<DocEmailDraft>("/api/email/draft", { kind, id, link, purpose }), retry: false, staleTime: Infinity });
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  useEffect(() => {
    const d = draft.data;
    if (!d) return;
    setTo(d.to);
    setCc(d.cc.join(", "));
    setSubject(d.subject);
    setMessage(d.message);
  }, [draft.data]);
  const ccList = cc
    .split(/[,;\s]+/)
    .map((x) => x.trim())
    .filter(Boolean);
  const send = useMutation({
    mutationFn: () => post<EmailItem>("/api/email/send", { kind, id, link, purpose, to: to.trim(), cc: ccList, subject, message }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["job-emails", jobId] }),
  });
  const d = draft.data;
  const mailto = d ? `mailto:${encodeURIComponent(to.trim())}?${ccList.length ? `cc=${encodeURIComponent(ccList.join(","))}&` : ""}subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(`${message}\n\n${d.button}: ${d.link}`)}` : "";

  if (send.isSuccess) {
    return (
      <Dialog title="Email sent" onClose={onClose} footer={<Button variant="primary" onClick={onClose}>Done</Button>}>
        <Alert tone="ok">
          Sent to {[send.data.to, ...send.data.cc].flat().join(", ")}
          {send.data.redirectedTo ? ` (staging: it went to ${send.data.redirectedTo} instead)` : ""}. You'll get a copy, and it's on the job's Communication tab.
        </Alert>
      </Dialog>
    );
  }
  return (
    <Dialog
      wide
      title="Email it"
      onClose={onClose}
      footer={
        d && (
          <>
            <Button variant="quiet" onClick={onClose}>
              Cancel
            </Button>
            {d.sending.ready ? (
              <Button variant="primary" busy={send.isPending} disabled={!to.trim() || !subject.trim() || !message.trim()} onClick={() => send.mutate()}>
                Send
              </Button>
            ) : (
              <a href={mailto} className="inline-flex items-center rounded-[var(--radius-control)] bg-brand px-4 py-2 text-sm font-semibold text-white hover:opacity-90">
                Open in my mail app
              </a>
            )}
          </>
        )
      }
    >
      {draft.isLoading ? (
        <Loading />
      ) : draft.error || !d ? (
        <Alert>{errorMessage(draft.error)}</Alert>
      ) : (
        <div className="flex flex-col gap-3">
          {!d.sending.ready && <Alert tone="info">{d.sending.reason}</Alert>}
          {send.error && <Alert>{errorMessage(send.error)}</Alert>}
          <p className="text-sm text-muted">From {d.from}. You get a copy, and replies come to you.</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="To" type="email" value={to} onChange={(e) => setTo(e.target.value)} autoComplete="off" />
            <Field label="CC (optional)" value={cc} onChange={(e) => setCc(e.target.value)} hint="Separate addresses with commas." autoComplete="off" />
          </div>
          <Field label="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
          <TextAreaField label="Message" value={message} onChange={(e) => setMessage(e.target.value)} rows={10} />
          <div className="rounded-[var(--radius-control)] bg-mist px-3 py-2 text-sm">
            Added below your message: a <b>{d.button}</b> button with the link.
          </div>
        </div>
      )}
    </Dialog>
  );
}

const STATUS_LABEL: Record<EmailItem["status"], { label: string; tone: "ok" | "wait" | "off" | "brand" }> = {
  sent: { label: "Sent", tone: "ok" },
  received: { label: "Received", tone: "off" },
  failed: { label: "Didn't send", tone: "brand" },
  bounced: { label: "Bounced", tone: "brand" },
  complaint: { label: "Marked as spam", tone: "brand" },
};

/** The job's emails (from "Email it"), on its Communication tab. */
export function JobEmails({ jobId }: { jobId: string }) {
  const q = useQuery({ queryKey: ["job-emails", jobId], queryFn: () => get<EmailItem[]>(`/api/jobs/${jobId}/emails`) });
  const [open, setOpen] = useState<string | null>(null);
  if (!q.data?.length) return null;
  return (
    <Panel>
      <PanelHeader title="Emails" description="Proposals, change orders, and invoices emailed from the CRM." />
      <ul>
        {q.data.map((e) => (
          <li key={e.id} className="border-b border-mist px-5 py-3 text-sm last:border-0">
            <button className="flex w-full flex-wrap items-center gap-2 text-left" onClick={() => setOpen(open === e.id ? null : e.id)}>
              <Badge tone={STATUS_LABEL[e.status].tone}>{STATUS_LABEL[e.status].label}</Badge>
              {e.docLabel && <span className="text-muted">{e.docLabel}</span>}
              <span className="min-w-0 flex-1 truncate font-semibold">{e.subject}</span>
              <span className="text-muted">
                {e.direction === "in" ? `from ${e.from}` : `to ${e.to.join(", ")}`}
                {e.direction === "out" && e.cc.length ? ` (cc ${e.cc.join(", ")})` : ""} · {e.sentBy?.name ? `${e.sentBy.name} · ` : ""}
                {formatDateTime(e.at)}
              </span>
            </button>
            {e.error && <p className="mt-1 text-danger">{e.error}</p>}
            {e.redirectedTo && <p className="mt-1 text-muted">Staging: went to {e.redirectedTo} instead.</p>}
            {open === e.id && <p className="mt-2 whitespace-pre-wrap rounded-[var(--radius-control)] bg-mist px-3 py-2">{e.body}</p>}
          </li>
        ))}
      </ul>
    </Panel>
  );
}

/** "Text it": opens the text window for a document whose customer link is in hand. */
export function TextItButton({ kind, id, link, jobId, className }: { kind: EmailDocKind; id: string; link: string; jobId: string; className?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button className={className} onClick={() => setOpen(true)}>
        Text it
      </Button>
      {open && <TextDialog kind={kind} id={id} link={link} jobId={jobId} onClose={() => setOpen(false)} />}
    </>
  );
}

/** To and the message from the template (editable); the link goes at the end. Sent through RingCentral and kept on the job. */
export function TextDialog({ kind, id, link, jobId, onClose, purpose }: { kind: EmailDocKind; id: string; link: string; jobId: string; onClose: () => void; purpose?: "overdue" }) {
  const qc = useQueryClient();
  const draft = useQuery({ queryKey: ["text-draft", kind, id, link, purpose], queryFn: () => post<DocTextDraft>("/api/text/draft", { kind, id, link, purpose }), retry: false, staleTime: Infinity });
  const [to, setTo] = useState("");
  const [message, setMessage] = useState("");
  useEffect(() => {
    if (!draft.data) return;
    setTo(draft.data.to);
    setMessage(draft.data.message);
  }, [draft.data]);
  const send = useMutation({
    mutationFn: () => post<{ messageId: string; redirectedTo: string | null }>("/api/text/send", { kind, id, link, purpose, to: to.trim(), message }),
    onSuccess: () => Promise.all([qc.invalidateQueries({ queryKey: ["comms"] }), qc.invalidateQueries({ queryKey: ["job-comms", jobId] })]),
  });
  const d = draft.data;
  const full = d ? `${message} ${d.link}` : "";
  const smsHref = `sms:${to.replace(/[^\d+]/g, "")}?&body=${encodeURIComponent(full)}`;
  if (send.isSuccess) {
    return (
      <Dialog title="Text sent" onClose={onClose} footer={<Button variant="primary" onClick={onClose}>Done</Button>}>
        <Alert tone="ok">Sent to {to}{send.data.redirectedTo ? ` (staging: it went to ${send.data.redirectedTo} instead)` : ""}. It's on the job's Communication tab, and replies come back there.</Alert>
      </Dialog>
    );
  }
  return (
    <Dialog
      title="Text it"
      onClose={onClose}
      footer={
        d && (
          <>
            <a href={smsHref} className="mr-auto inline-flex items-center px-2 py-2 text-sm text-muted hover:text-ink">
              Open in my phone instead
            </a>
            <Button variant="quiet" onClick={onClose}>
              Cancel
            </Button>
            {d.sending.ready && (
              <Button variant="primary" busy={send.isPending} disabled={!to.trim() || !message.trim()} onClick={() => send.mutate()}>
                Send text
              </Button>
            )}
          </>
        )
      }
    >
      {draft.isLoading ? (
        <Loading />
      ) : draft.error || !d ? (
        <Alert>{errorMessage(draft.error)}</Alert>
      ) : (
        <div className="flex flex-col gap-3">
          {!d.sending.ready && <Alert tone="info">{d.sending.reason}</Alert>}
          {send.error && <Alert>{errorMessage(send.error)}</Alert>}
          {d.from && <p className="text-sm text-muted">From {d.from}. Replies come back to the job's Communication tab.</p>}
          <Field label="Their cell" type="tel" value={to} onChange={(e) => setTo(e.target.value)} autoComplete="off" />
          <TextAreaField label="Message" value={message} onChange={(e) => setMessage(e.target.value)} rows={4} />
          <p className="text-sm text-muted">The link is added at the end: {d.link}</p>
        </div>
      )}
    </Dialog>
  );
}

export type SendHow = "email" | "text" | "link";

const HOW_LABELS: Record<SendHow, string> = { email: "Email it", text: "Text it", link: "Just give me the link" };

/** How to get it to the customer, picked before sending. */
export function HowToSend({ value, onChange }: { value: SendHow; onChange: (v: SendHow) => void }) {
  return (
    <fieldset>
      <legend className="mb-1 text-sm font-semibold text-ink-soft">How to send it</legend>
      <div role="radiogroup" className="inline-flex flex-wrap rounded-[var(--radius-control)] bg-mist p-0.5">
        {(Object.keys(HOW_LABELS) as SendHow[]).map((h) => (
          <button
            key={h}
            type="button"
            role="radio"
            aria-checked={value === h}
            onClick={() => onChange(h)}
            className={cx("rounded-[6px] px-3 py-1.5 text-sm font-semibold", value === h ? "bg-white shadow-sm" : "text-muted hover:text-ink")}
          >
            {HOW_LABELS[h]}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

/** Once the link is made: the email or text window, filled in; or the link to copy (with Email it and Text it). */
export function AfterSend({ how, kind, id, link, jobId, onClose, linkNote, purpose }: { how: SendHow; kind: EmailDocKind; id: string; link: string; jobId: string; onClose: () => void; linkNote?: string; purpose?: "overdue" }) {
  const [copied, setCopied] = useState(false);
  if (how === "email") return <EmailDialog kind={kind} id={id} link={link} jobId={jobId} onClose={onClose} purpose={purpose} />;
  if (how === "text") return <TextDialog kind={kind} id={id} link={link} jobId={jobId} onClose={onClose} purpose={purpose} />;
  return (
    <Dialog title={`${EMAIL_DOC_LABELS[kind]} ready`} onClose={onClose} footer={<Button variant="primary" onClick={onClose}>Done</Button>}>
      <div className="flex flex-col gap-3">
        <p className="text-sm text-ink-soft">{linkNote ?? "Send this link to the customer. Copy it now: for security it can't be shown again."}</p>
        <input readOnly value={link} onFocus={(e) => e.target.select()} className="w-full rounded-[var(--radius-control)] border border-line bg-mist px-3 py-2 text-sm" />
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={async () => {
              await navigator.clipboard.writeText(link);
              setCopied(true);
            }}
          >
            {copied ? "Copied" : "Copy link"}
          </Button>
          <TextItButton kind={kind} id={id} link={link} jobId={jobId} />
          <EmailItButton kind={kind} id={id} link={link} jobId={jobId} />
        </div>
      </div>
    </Dialog>
  );
}

/**
 * "Send to customer…" for invoices and change orders: pick email, text, or just the link; sending
 * makes the link (and does what sending does: marked sent, QuickBooks), then the window for that opens.
 */
export function SendToCustomer({ kind, id, jobId, note, send, onClose, onSent, purpose, title = "Send to customer" }: { kind: EmailDocKind; id: string; jobId: string; note: string; send: () => Promise<string>; onClose: () => void; onSent?: () => void; purpose?: "overdue"; title?: string }) {
  const [how, setHow] = useState<SendHow>("email");
  const [link, setLink] = useState<string | null>(null);
  const go = useMutation({
    mutationFn: send,
    onSuccess: (l) => {
      setLink(l);
      onSent?.();
    },
  });
  if (link) return <AfterSend how={how} kind={kind} id={id} link={link} jobId={jobId} onClose={onClose} purpose={purpose} />;
  return (
    <Dialog
      title={title}
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={go.isPending} onClick={() => go.mutate()}>
            Continue
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {go.error && <Alert>{errorMessage(go.error)}</Alert>}
        <HowToSend value={how} onChange={setHow} />
        <p className="text-sm text-muted">{note}</p>
      </div>
    </Dialog>
  );
}
